import React, { useContext, useState } from 'react';
import { Container, Row, TabContent, TabPane } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import OperationContext from '../context';
import reducer from './historyReducer';
import {
  Button,
  ConfirmModal,
  PageHeader,
  Section,
  SpinnerArea,
  TabBar,
  Table,
} from '../../../../shared/components';
import { Cell } from '../../../../shared/components/Table';
import { OpMember, OpMemberReq } from '../../../../services/types/Member';
import { customDateStr, objectsAreEquals } from '../../../../shared/misc/utils';
import { KeyMatch } from '../../../../types';
import AddMemberPopup from './AddMemberPopup';
import MainContext from '../../../App/MainProvider/context';
import Role from '../../../../types/role';
import operMemberService from '../../../../services/operMemberService';
import RouteLeavingGuard from '../../../App/RouteLeavingGuard';
import HistoryTable from './HistoryTable';
import useServiceReducer from '../../../../shared/hooks/useServiceReducer';

const Members: React.FC = () => {
  const location = useLocation();
  const { user, service, notify, inSuperUserMode, inGuidanceServiceMode } = useContext(MainContext);
  const { operation, reload: reloadOperationData } = useContext(OperationContext);
  const { t } = useTranslation();
  const [memberHistory, loadingHistory, historyFilter, dispatch] = useServiceReducer(
    operMemberService.getHistory,
    reducer,
    {
      operationId: operation.id,
      pageNumber: 1,
      pageSize: 10,
      sortDescending: true,
      sortField: 'ToDate',
    },
    [inSuperUserMode(), inGuidanceServiceMode()]
  );

  const [postData, setPostData] = useState<OpMember[]>(
    [...operation.memberList].map((x) => ({ ...x }))
  );
  const [formDirty, setFormDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [tabSelected, setTabSelected] = useState(1);
  const initSort = {
    sortField: 'isPrimary',
    sortDescending: false,
  };
  const [filter, setFilter] = useState(initSort);
  const [addVisible, setAddVisible] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const verifyDirty = (members: OpMember[]) => {
    if (
      members.length !== operation.memberList.length ||
      members.some((member) => {
        const initMember = operation.memberList.find((x) => x.id === member.id);
        return !initMember || !objectsAreEquals(member, initMember);
      })
    ) {
      setFormDirty(true);
    } else {
      setFormDirty(false);
    }
  };

  const makePrimary = (memberId: number) => {
    const members = [...postData];
    const currentPrimaryMember = members.find((x) => x.isPrimary);
    if (currentPrimaryMember) {
      currentPrimaryMember.isPrimary = false;
    }
    const newPrimary = members.find((x) => x.id === memberId);
    if (newPrimary) {
      newPrimary.isPrimary = true;
    }
    setPostData(members);
    verifyDirty(members);
  };

  const addMember = (member: OpMember) => {
    if (member) {
      const members = [...postData];
      if (members.some((x) => x.id === member.id)) {
        return;
      }

      const existingMember = operation.memberList.find((x) => x.id === member.id);
      const newMember = existingMember
        ? { ...member, assignedDate: existingMember.assignedDate }
        : member;
      setPostData([...postData, newMember]);
      verifyDirty([...postData, newMember]);
    }
  };

  const removeMember = (memberId: number) => {
    const filteredMembers = [...postData].filter((x) => x.id !== memberId);
    setPostData(filteredMembers);

    verifyDirty(filteredMembers);
  };

  const sortPostData = (descending: boolean, field: KeyMatch<OpMember, string>) => {
    if (postData) {
      const sortedList = [...postData].sort((a, b) => {
        if ((descending && a[field] === null) || (!descending && b[field] === null)) {
          return -1;
        }
        if ((descending && b[field] === null) || (!descending && a[field] === null)) {
          return 1;
        }
        if (field === ('assignedDate' as KeyMatch<OpMember, string>)) {
          if (
            (descending && new Date(a[field]).getTime() < new Date(b[field]).getTime()) ||
            (!descending && new Date(a[field]).getTime() > new Date(b[field]).getTime())
          ) {
            return -1;
          }
          if (
            (descending && new Date(a[field]).getTime() > new Date(b[field]).getTime()) ||
            (!descending && new Date(a[field]).getTime() < new Date(b[field]).getTime())
          ) {
            return 1;
          }
        } else {
          if ((descending && a[field] < b[field]) || (!descending && a[field] > b[field])) {
            return field === ('isPrimary' as KeyMatch<OpMember, string>) ? 1 : -1;
          }
          if ((descending && a[field] > b[field]) || (!descending && a[field] < b[field])) {
            return field === ('isPrimary' as KeyMatch<OpMember, string>) ? -1 : 1;
          }
        }
        return 0;
      });
      setPostData(sortedList);
    }
  };

  const changeSort = (sortField: string, sortDescending: boolean) => {
    setFilter({ ...filter, sortField, sortDescending });
    sortPostData(!sortDescending, sortField as KeyMatch<OpMember, string>);
  };

  const tabs = [
    { id: 1, text: t('currentMembers'), htmlId: 'tabCurrents' },
    { id: 2, text: t('previousMembers'), htmlId: 'tabPrevious' },
  ];

  const canEdit =
    user.roleCode === Role.GroupHead || user.roleCode === Role.HSO || inSuperUserMode();
  const getTextSetPrimary = (member: OpMember) => {
    if (member.isPrimary) {
      return t('yes');
    }
    return canEdit && member.id > 0 ? t('setAsPrimary') : 'No';
  };

  const currentMembersCells: Cell<OpMember>[] = [
    {
      name: 'fullName',
      column: 'userName',
      value: (x) => x.fullName,
      css: 'accent w-25',
    },
    {
      name: 'group',
      column: 'group',
      value: (x) => x.group,
      // css: 'accent w-10'
    },
    {
      name: 'isPrimary',
      column: 'primary',
      value: (x) => (x.isPrimary ? 1 : 0),
      render: (_, member) =>
        member.isPrimary || !canEdit || member.id <= 0 ? (
          <>{getTextSetPrimary(member)}</>
        ) : (
          <Button
            text={getTextSetPrimary(member)}
            className="btn-tertiary p-0"
            disabled={member.isPrimary || !canEdit}
            onClick={() => makePrimary(member.id)}
          />
        ),
    },
    {
      name: 'assignedDate',
      column: 'assignedDate',
      value: (x) => customDateStr(x.assignedDate),
    },
    {
      name: 'rationale',
      column: 'rationale',
      value: (x) => x.rationale,
      css: 'w-50',
      sortDisabled: true,
    },
    {
      column: 'remove',
      render: (_, m) =>
        canEdit && (!m.isPrimary || postData.length === 1) ? (
          <Button
            className="btn-tertiary no-padding"
            icon="trash-alt"
            text=""
            onClick={() => removeMember(m.id)}
          />
        ) : (
          <></>
        ),
      css: 'text-center',
    },
  ];

  const applyChanges = () => {
    const data = postData.map(
      (x) => ({ id: x.id, isPrimary: x.isPrimary, rationale: x.rationale } as OpMemberReq)
    );
    service(() => operMemberService.saveMembers(operation.id, data), {
      callback: () => {
        notify(t('operationMembers'), 'Saved Successfully', 'success');
        reloadOperationData();
        dispatch({ type: 'RELOAD' });
        setFormDirty(false);
      },
      loader: setIsSubmitting,
    });
  };

  const changeHistorySort = (sortField: string, sortDescending: boolean) => {
    dispatch({ type: 'ORDER', column: sortField, descending: sortDescending });
  };
  const changePage = (page: number) => dispatch({ type: 'PAGE', value: page });

  const changePageSize = (size: number) => dispatch({ type: 'PAGE_SIZE', value: size });

  return (
    <>
      <RouteLeavingGuard
        when={formDirty}
        shouldBlockNavigation={() => formDirty}
        from={location}
        message={t('dirtyAdminPopupMsg')}
      />
      <Container>
        <PageHeader
          title={`Operation ${operation.number} / Team Members`}
          side={
            canEdit ? (
              <>
                <Button
                  text={t('addESGMember')}
                  className="btn-primary"
                  onClick={() => setAddVisible(true)}
                  htmlId="btn_add"
                  disabled={isSubmitting}
                />
                <Button
                  text={t('applyChanges')}
                  className="btn-primary ml-0"
                  onClick={() => setShowConfirmModal(true)}
                  htmlId="btn_apply"
                  disabled={isSubmitting || !formDirty}
                />
              </>
            ) : undefined
          }
        />
        <Row>
          <Section>
            <SpinnerArea loading={isSubmitting}>
              {postData && (
                <>
                  <div>
                    <TabBar
                      id="operationMembers"
                      tabSelected={tabSelected}
                      setTabSelected={setTabSelected}
                      headers={tabs}
                    />
                    {/* <div className="d-flex mt-n5">
                    <div className="mr-auto" />
                    <Button
                      htmlId="btnExcel"
                      text={t('exportToExcel')}
                      onClick={() => {}}
                      icon="file-excel"
                      className="btn-tertiary"
                    />
                  </div> */}
                  </div>
                  <TabContent activeTab={tabSelected}>
                    <TabPane tabId={1}>
                      <Table
                        data={postData}
                        cells={currentMembersCells}
                        keyFunc={(x) => x.id}
                        sortData={filter}
                        changeSort={changeSort}
                        discapitalize
                        wrapperClass="my-3"
                      />
                    </TabPane>
                    <TabPane tabId={2}>
                      <SpinnerArea loading={loadingHistory}>
                        {memberHistory && (
                          <HistoryTable
                            historyContent={memberHistory}
                            filter={historyFilter}
                            changeSort={changeHistorySort}
                            changePage={changePage}
                            changePageSize={changePageSize}
                          />
                        )}
                      </SpinnerArea>
                    </TabPane>
                  </TabContent>
                </>
              )}
            </SpinnerArea>
          </Section>
        </Row>
        <AddMemberPopup
          onSubmit={(member) => member && addMember(member)}
          visible={addVisible}
          setVisible={setAddVisible}
          onList={postData}
        />
        <ConfirmModal
          visible={showConfirmModal}
          buttonText={t('confirmChanges')}
          setVisible={setShowConfirmModal}
          action={applyChanges}
        >
          {t('confirmChangesMessage')}
        </ConfirmModal>
      </Container>
    </>
  );
};

export default Members;
